// Offline Queue Manager - handles syncing queued requests when back online
import { getQueuedRequests, removeFromQueue, getQueueLength, addToQueue } from './db';
import { toast } from 'sonner';

const MAX_RETRIES = 3;
let isSyncing = false;

export async function processQueue(): Promise<void> {
  if (isSyncing || !navigator.onLine) return;

  isSyncing = true;
  try {
    const queueLength = await getQueueLength();

    if (queueLength === 0) {
      return;
    }

    // FRISCHEN Token zum Sync-Zeitpunkt besorgen — der beim Einreihen
    // serialisierte Token ist nach längerer Offline-Phase meist abgelaufen.
    // Hat der Nutzer sich inzwischen neu angemeldet, gelingen die Requests jetzt
    // statt am alten Token zu scheitern. Dynamischer Import vermeidet den Zyklus
    // offlineQueue ↔ api; defensiv, damit Tests/Edge-Cases ohne localStorage nicht brechen.
    let freshToken: string | null = null;
    try {
      const { getToken } = await import('./api');
      freshToken = getToken();
    } catch (err) {
      console.warn('[offlineQueue] Token konnte nicht gelesen werden:', err);
    }

    toast.info(`Synchronisiere ${queueLength} gespeicherte Anfragen...`);

    const requests = await getQueuedRequests();
    let successCount = 0;
    let failCount = 0;

    for (const request of requests) {
      // Frischen Token einsetzen, falls vorhanden — sonst die ursprünglichen
      // Header weiterverwenden (Verhalten wie zuvor).
      const headers = freshToken
        ? { ...request.headers, Authorization: `Bearer ${freshToken}` }
        : request.headers;

      try {
        const response = await fetch(request.url, {
          method: request.method,
          headers,
          body: request.body,
        });

        if (response.ok) {
          await removeFromQueue(request.id);
          successCount++;
        } else if (response.status === 401 || response.status === 403) {
          // Auth error - remove from queue (user must re-login)
          await removeFromQueue(request.id);
          failCount++;
          toast.warning('Eine gespeicherte Anfrage konnte nicht übertragen werden (Anmeldung abgelaufen). Bitte Aktion nach erneutem Login wiederholen.');
        } else if (request.retries < MAX_RETRIES) {
          // Retry later - Zähler hochsetzen, sonst bleibt der Request für immer in der Queue
          await addToQueue({ ...request, retries: request.retries + 1 });
          failCount++;
        } else {
          // Max retries reached - remove from queue
          await removeFromQueue(request.id);
          failCount++;
          toast.error('Eine gespeicherte Anfrage wurde nach mehreren Fehlversuchen verworfen. Bitte Aktion manuell wiederholen.');
        }
      } catch (error) {
        // Network error - keep in queue for later (retries zählt hier bewusst NICHT hoch:
        // kein Netz ist kein Fehler des Requests, er soll beim nächsten Online-Gehen wieder dran sein)
        console.error('Queue sync error:', error);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Anfragen synchronisiert`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} Anfragen fehlgeschlagen`);
    }
  } finally {
    isSyncing = false;
  }
}

// Start listening for online events
export function initOfflineSync(): void {
  window.addEventListener('online', () => {
    toast.info('Wieder online');
    processQueue();
    void syncZosOutboxSafe();
  });

  window.addEventListener('offline', () => {
    toast.warning('Offline - Daten werden lokal gespeichert');
  });

  // Process queue on startup if online
  if (navigator.onLine) {
    processQueue();
    void syncZosOutboxSafe();
  }
}

/** ZOS-Antworten-Outbox syncen (Wizard-Eingaben die offline entstanden sind).
 *  Dynamischer Import um Zyklus offlineQueue ↔ api zu vermeiden. */
async function syncZosOutboxSafe(): Promise<void> {
  try {
    const { syncZosAnswerOutbox } = await import('./api');
    await syncZosAnswerOutbox();
  } catch (err) {
    console.warn('[offlineQueue] ZOS-Outbox-Sync fehlgeschlagen:', err);
  }
}

// Get current queue status
export async function getQueueStatus(): Promise<{ pending: number; isSyncing: boolean }> {
  const pending = await getQueueLength();
  return { pending, isSyncing };
}
